// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconVideoPause from "@stoneage/XDL/Icons/IconVideoPause";
import IconVideoPlay from "@stoneage/XDL/Icons/IconVideoPlay";
import { getVideoEmbeddedUrl, xScreen } from "@stoneage/XDL/Utilities";
import React, { useEffect, useRef } from "react";
import { css } from "styled-components";

export interface VideoProps {
    src: string;
    controls?: boolean;
    extension?: string;
    autoPlay?: boolean;
    muted?: boolean;
    repeat?: boolean;
    extendedStyles?: VideoStyles;
}

export interface VideoStyles {
    videoWrapper?: InjectableCss;
    toggleVideoButton?: ButtonPresentationProps;
    toggleIcon?: IconProps;
}

export const videoStyles: VideoStyles = {
    videoWrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 100%;
        `,
    },
    toggleVideoButton: {
        variant: "link" as any,
        css: css`
            position: absolute;
            right: 32px;
            bottom: 24px;
            z-index: 1000;

            ${xScreen("lte", "md")} {
                right: 24px;
            }

            ${xScreen("lte", "sm")} {
                right: 16px;
            }
        `,
    },
    toggleIcon: {
        size: 24,
        color: "common.white",
    },
};

type Props = VideoProps;

const Video: React.FC<Props> = ({
    src,
    controls = true,
    muted,
    extension = "mp4",
    autoPlay,
    repeat,
    extendedStyles,
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [styles] = React.useState(() => mergeToNew(videoStyles, extendedStyles));
    const [isPlaying, setIsPlaying] = React.useState(false);

    const togglePlay = () => {
        if (!videoRef.current) return;

        if (videoRef.current.paused) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    useEffect(() => {
        if (autoPlay && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            setIsPlaying(true);
        }
    }, [autoPlay]);

    if (!src) {
        return null;
    }

    const withExtension = src.includes(extension);
    const embeddedURL = !withExtension ? getVideoEmbeddedUrl(src) : "";

    return withExtension ? (
        <StyledWrapper {...styles.videoWrapper}>
            <video
                ref={videoRef}
                width="100%"
                height="100%"
                controls={controls}
                muted={muted}
                loop={repeat}
                playsInline={true} // for ios
                preload="metadata" // for ios
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            >
                <track kind="captions" src="sampleCaptions.vtt" srcLang="en" />
                <source src={src} type={`video/${extension}`}></source>
            </video>

            <Button {...styles.toggleVideoButton} onClick={togglePlay}>
                <ButtonIcon {...styles.toggleIcon} src={isPlaying ? IconVideoPause : IconVideoPlay} />
            </Button>
        </StyledWrapper>
    ) : (
        <iframe
            src={embeddedURL}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
            style={{ border: "none" }}
        />
    );
};

export default Video;
